// 3rd party libs
import i18next from 'i18next';
import { TFunction } from 'i18next';

// Types
import { Product } from 'types/parts';
import { CartAction, CartState, CartPurchasable, CartVerificationComment } from 'types/parts/cart';

// Functions
import { saveAs } from 'file-saver';

// Actions
import { cartActions } from 'actions';
import { cloneDeep, cond, sum } from 'lodash';
import { getPartialLeadTimeText, getStockText, isCartExpired } from 'common/helpers/v1';
import { LOCAL_STORAGE_CART_KEY_PREFIX, STOCK_WARNING_PERCENTAGE } from 'constants/cart';

// Analytics
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';

// Constants
import { UNIVERSAL_PRODUCTS_KEY } from 'constants/cart';
import { SFCart, SFCartItem, SFCartSummary } from 'types/parts/cart';

// Helpers
import { addQuotes } from 'common/helpers/v1/strings';
import { sfEnabled } from 'constants/featureFlags';
import { ToastMsg } from 'common/components/Toast';
import { isPriceMissing } from 'components/CustomerPrice';

export const defaultSFCartState = {
  cartItems: new Array<SFCartItem>(),
  cartSummary: {} as SFCartSummary
} as SFCart;

export const defaultCartState = {
  ids: new Array<string>(),
  items: new Array<CartPurchasable>(),
  lastUpdatedTimestamp: Date.now(),
  sfCart: defaultSFCartState,
  userId: JSON.parse(sessionStorage.getItem('user') as string)?.id
} as CartState;

function formatItemToastText(item: Product): string {
  return `${((item.sku || item.description) as string).slice(0, 64)}`;
}

function unverifyCart(cartState: CartState): CartState {
  for (const item of cartState.items) {
    item.verified = false;
    item.verificationComments = [];
  }
  cartState.lastVerifiedTimestamp = undefined;
  return cartState;
}

// function getCartFromLocalStorage(userId: string): CartState {
//   const cartKey = `${LOCAL_STORAGE_CART_KEY_PREFIX}-${userId}`;
//   const cartString = localStorage.getItem(cartKey);
//   if (cartString != null) {
//     const savedCart: CartState | undefined = JSON.parse(localStorage.getItem(cartKey) ?? '');
//     if (savedCart && isCartExpired(savedCart)) {
//       ToastMsg({
//         heading: 'Info',
//         message: i18next.t('saved_cart_expired', { ns: 'fpns' }),
//         type: 'info'
//       });
//       localStorage.removeItem(cartKey);
//     } else if (savedCart) {
//       return {
//         userId: userId,
//         ...unverifyCart(savedCart)
//       };
//     }
//   }
//   return {
//     ...defaultCartState
//   };
// }

// export function persistCartInLocalStorage(cartState: CartState): void {
//   cartState.lastUpdatedTimestamp = Date.now();
//   if (cartState.userId) {
//     const cartKey = `${LOCAL_STORAGE_CART_KEY_PREFIX}-${cartState.userId}`;
//     localStorage.setItem(cartKey, JSON.stringify(cartState));
//   } else {
//     console.error("User id not set -- can't save cart!");
//   }
// }

const setSFCart = (cartState: CartState, sfCart: SFCart): CartState => {
  cartState.sfCart = sfCart;
  return cartState;
};

function addToCart(
  cartState: CartState,
  item: Product,
  quantity = 1,
  groupId?: string,
  groupDescription?: string,
  hideToast?: boolean
): CartState {
  if (quantity <= 0) {
    console.error('Cannot add zero or less of a product to the cart.');
    return cartState;
  }
  // can't serialize a Set in redux, so we have to convert back and forth to store it
  const idSet = new Set<string>(cartState.ids);
  const currentGroupId = groupId || UNIVERSAL_PRODUCTS_KEY;
  const existingItemIndex = cartState.items.findIndex(
    (cartItem) =>
      cartItem.item.id === item.id &&
      cartItem.groupId === currentGroupId &&
      cartItem.item.priceUnit === item.priceUnit &&
      cartItem.item.price === item.price &&
      cartItem.item.custPrice === item.custPrice
  );
  // if the item is already in the set of items in the cart
  if (existingItemIndex !== -1) {
    // Add the new quantity to the existing item
    cartState.items[existingItemIndex].quantity += quantity;
    if (!hideToast) {
      ToastMsg({
        heading:
          i18next.t('added_to_cart', {
            item: `Part No '${formatItemToastText(item)}'`,
            ns: 'fpns',
            interpolation: { escapeValue: false }
          }) || '',
        message: '',
        type: 'info',
        position: 'top-right'
      });
    }
    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.ADD_TO_CART,
      event: ECommerceAnalyticEventActions.ADD_TO_CART,
      label: item.description,
      value: item.custPrice,
      currency: item.priceUnit,
      items: [
        {
          item_id: item.id,
          item_name: item.description,
          quantity: item.stock,
          price: item.custPrice,
          currency: item.priceUnit
        }
      ]
    });
    // if the item isn't already in our cart, add it as a new cart purchasable
  } else if (item.id) {
    idSet.add(item.id);
    cartState.items.push({
      item: item,
      quantity: quantity,
      verified: false,
      verificationComments: [],
      groupId: currentGroupId,
      groupDescription: groupDescription
    });

    if (!hideToast) {
      ToastMsg({
        heading:
          i18next.t('added_to_cart', {
            item: `Part No '${formatItemToastText(item)}'`,
            ns: 'fpns',
            interpolation: { escapeValue: false }
          }) || '',
        message: '',
        type: 'info',
        position: 'top-right'
      });
    }

    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.ADD_TO_CART,
      event: ECommerceAnalyticEventActions.ADD_TO_CART,
      label: item.description,
      value: item.custPrice,
      currency: item.priceUnit,
      items: [
        {
          item_id: item.id,
          item_name: item.description,
          quantity: item.stock,
          price: item.custPrice,
          currency: item.priceUnit
        }
      ]
    });
    if (quantity > STOCK_WARNING_PERCENTAGE * item.stock) {
      const currentGroupId = groupId || UNIVERSAL_PRODUCTS_KEY;
      applyOrRemoveLowStockWarning(
        cartState,
        cartState.items.findIndex(
          (i) => i.item.id === item.id && i.item.groupId === currentGroupId
        ),
        item.stock,
        quantity
      );
    }
  } else {
    ToastMsg({
      heading: 'Error',
      type: 'error',
      message: i18next.t('cannot_add_to_cart', {
        item: `${formatItemToastText(item) || i18next.t('item')}`,
        ns: 'fpns',
        interpolation: { escapeValue: false }
      })
    });
  }
  cartState.ids = Array.from(idSet.values());
  // persistCartInLocalStorage(cartState); // cart is backend facing. No need for frontend local storage
  return cartState;
}

function removeFromCart(
  cartState: CartState,
  item: Product,
  quantity: number,
  groupId?: string
): CartState {
  if (quantity <= 0) {
    console.error('Cannot remove zero or less of a product from the cart.');
    return cartState;
  }
  // can't serialize a Set in redux, so we have to convert back and forth to store it
  const idSet = new Set<string>(cartState.ids);

  const currentGroupId = groupId || UNIVERSAL_PRODUCTS_KEY;
  const existingItemIndex = cartState.items.findIndex(
    (cartItem) => cartItem.item.id === item.id && cartItem.groupId === currentGroupId
  );
  
  if (existingItemIndex !== -1) {
    // if removing the quantity requested would take the
    // remaining quantity to 0 or below, just remove it entirely
    if (cartState.items[existingItemIndex].quantity - quantity <= 0) {
      cartState.items.splice(existingItemIndex, 1);

      // Also, remove its ID from idSet if no other item has its ID
      if (cartState.items.findIndex((cartItem) => cartItem.item.id === item.id) === -1) {
        idSet.delete(item.id);
      }
    } else {
      cartState.items[existingItemIndex].quantity -= quantity;
    }
    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.REMOVE_FROM_CART,
      event: ECommerceAnalyticEventActions.REMOVE_FROM_CART,
      label: item.description,
      currency: item.priceUnit,
      value: item.custPrice,
      items: [
        {
          item_id: item.id,
          item_name: item.description,
          quantity: item.stock,
          price: item.custPrice,
          currency: item.priceUnit
        }
      ]
    });
  } else {
    console.error(
      'And you may find yourself removing an item from your cart which you never added... \n And you may ask yourself... how did I get here?'
    );
  }
  ToastMsg({
    heading: 'Info',
    message: `\u00A0${quantity}\u00A0${formatItemToastText(item)} ${i18next.t('removed_from_cart', {
      item: '',
      ns: 'fpns'
    })}`,
    type: 'info'
  });
  return cartState;
}

function updateItemQuantity(
  cartState: CartState,
  item: Product,
  quantity: number,
  groupId?: string
): CartState {
  if (quantity < 0) {
    console.error('Cannot update quantity of a product to less than 0.');
    return cartState;
  }

  const currentGroupId = groupId || UNIVERSAL_PRODUCTS_KEY;
  const existingItemIndex = cartState.items.findIndex(
    (cartItem) =>
      cartItem.item.id === item.id &&
      cartItem.groupId === currentGroupId &&
      cartItem.item.price === item.price &&
      cartItem.item.custPrice === item.custPrice
  );
  if (existingItemIndex === -1) {
    console.error("Cannot update an item's quantity if it isn't in the cart at all.");
    return cartState;
  }

  cartState.items[existingItemIndex].quantity = quantity;
  applyOrRemoveLowStockWarning(cartState, existingItemIndex, item.stock, quantity); // this uses the passed item, which should always be more up to date than the cart saved version.
  return cartState;
}

function applyOrRemoveLowStockWarning(
  cartState: CartState,
  itemToUpdateIndex: number,
  stock: number,
  quantity: number
) {
  const itemHasLowStockWarning =
    cartState.items[itemToUpdateIndex]?.verificationComments.findIndex(
      (c) => c === CartVerificationComment.LOW_STOCK
    ) !== -1;
  if (quantity > STOCK_WARNING_PERCENTAGE * stock && !itemHasLowStockWarning) {
    cartState.items[itemToUpdateIndex].verificationComments.push(CartVerificationComment.LOW_STOCK);
  } else if (quantity <= STOCK_WARNING_PERCENTAGE * stock) {
    cartState.items[itemToUpdateIndex].verificationComments = cartState.items[
      itemToUpdateIndex
    ].verificationComments.filter((c) => c != CartVerificationComment.LOW_STOCK);
  }
}

const updateCartItem = (
  cartState: CartState,
  item: Product,
  urgent: boolean,
  groupId?: string,
  procareAllowance?: boolean,
  selected?: boolean
): CartState => {
  const itemGroupId = groupId || UNIVERSAL_PRODUCTS_KEY;
  const itemToUpdateIndex = cartState.items.findIndex(
    (i) =>
      i.item.id === item.id &&
      i.groupId === itemGroupId &&
      i.item.priceUnit === item.priceUnit &&
      i.item.custPrice === item.custPrice &&
      i.item.price === item.price &&
      i.item.uniqueId === item.uniqueId
  );
  cartState.items[itemToUpdateIndex].urgent = urgent;
  cartState.items[itemToUpdateIndex].procareAllowance = procareAllowance;
  cartState.items[itemToUpdateIndex].selected = selected;
  return cartState;
};
const updateCartItems = (cartState: CartState, updatedItems: Product[]): CartState => {
  // replace items in cartState that match the updatedItems by id, groupId, and uniqueId with the updated items
  updatedItems.forEach((item) => {
    if (item) {
      const itemGroupId = item.groupId || UNIVERSAL_PRODUCTS_KEY;
      const itemToUpdateIndex = cartState.items.findIndex(
        (i) =>
          i.item.id === item.id && i.groupId === itemGroupId && i.item.uniqueId === item.uniqueId
      );
      if (itemToUpdateIndex !== -1) {
        cartState.items[itemToUpdateIndex].item = item;
      }
    }
  });
  return cartState;
};
function emptyCart(originalCartState: CartState): CartState {
  if (originalCartState.items.length !== 0) {
    ToastMsg({
      heading: 'Info',
      message: i18next.t('all_cart_items_removed', { ns: 'fpns' }),
      type: 'info'
    });
    generateAnalyticsEvent({
      category: AnalyticsCategories.ECOMMERCE,
      action: ECommerceAnalyticEventActions.EMPTY_CART,
      value: sum(originalCartState.items.map((item) => item.quantity))
    });
  }

  return defaultCartState;
}

function addVerificationCommentToCartPurchasable(
  originalPurchasable: CartPurchasable,
  verificationItem: Product
): void {
  if (
    originalPurchasable.item.price &&
    verificationItem.price &&
    originalPurchasable.item.price < verificationItem.price
  ) {
    originalPurchasable.verificationComments.push(CartVerificationComment.PRICE_CHANGE);
  }
  if (originalPurchasable.item.stock > verificationItem.stock) {
    originalPurchasable.verificationComments.push(CartVerificationComment.STOCK_CHANGE);
  }
  if (originalPurchasable.item.leadTime < verificationItem.leadTime) {
    originalPurchasable.verificationComments.push(CartVerificationComment.LEAD_TIME_CHANGE);
  }
  originalPurchasable.verified = true;
}

function verifyCart(cartState: CartState, verificationItems: Product[] | undefined): CartState {
  if (verificationItems === undefined) {
    return defaultCartState;
  } else if (verificationItems) {
    for (const existingItem of cartState.items) {
      const verificationItem = verificationItems.find((p) => p.id === existingItem.item.id);
      if (!existingItem.verified && verificationItem) {
        const itemToUpdateIndex = cartState.items.findIndex(
          (i) => i.item.id === verificationItem.id
        );
        addVerificationCommentToCartPurchasable(existingItem, verificationItem);
        applyOrRemoveLowStockWarning(
          cartState,
          itemToUpdateIndex,
          verificationItem.stock,
          cartState.items[itemToUpdateIndex].quantity
        );
      }
    }
    cartState.lastVerifiedTimestamp = Date.now();
  }
  return cartState;
}

function downloadCartCSV(cartState: CartState, t: TFunction, languageId: string) {
  // for CSV purposes, adds optional quotes around 'str' and doubles internal quotes as per spec
  function priceOrEmpty(
    price: number | undefined,
    unit: string | undefined,
    isPurchasable?: boolean | undefined
  ): string {
    if (price === undefined || price === 0 || !isPurchasable) {
      return 'N/A';
    }
    return unit + price.toFixed(2);
  }

  const csv: BlobPart[] = [];
  csv.push(
    `${t('part_number')}, ${t('description', { ns: 'common' })},${t('stock', { ns: 'fpns' })},${t(
      'parts_table_avg_lead_time',
      { ns: 'fpns' }
    )},${t('quantity', {
      ns: 'common'
    })}, ${t('list_price', { ns: 'fpns' })}, ${t('customer_price', { ns: 'fpns' })}\n`
  );
  for (const item of cartState.items) {
    const multilingualDescObj = item.item.multilingualDesc;
    type MultiligualLanguageKey = keyof typeof multilingualDescObj;
    const multilingualDesc = multilingualDescObj
      ? multilingualDescObj[languageId as MultiligualLanguageKey]
      : undefined;
    const finalDescription = multilingualDesc
      ? multilingualDesc
      : item.item.description
      ? item.item.description
      : item.item.manualDescription || 'N/A';
    csv.push(
      `\t${item.item.sku},${addQuotes(finalDescription)},${getStockText(
        item.item
      )}, ${getPartialLeadTimeText(item.item.leadTime, t)},${item.quantity},` +
        `${priceOrEmpty(
          item.item.price,
          item.item.priceUnit,
          item.item.isPurchasable
        )},${priceOrEmpty(item.item.custPrice, item.item.priceUnit, item.item.isPurchasable)}\n`
    );
  }
  saveAs(new Blob(csv, { type: 'text/csv;charset=utf-8', endings: 'native' }), `cart-items.csv`);
}

const updateCart = (cartState: CartState, cartAction: CartAction) => {
  const { selected } = cartAction;
  const updatedCartItems = cartState.items.map((product) => ({ ...product, selected: selected }));

  return { ...cartState, items: updatedCartItems };
};

export default (cartState = defaultCartState, action: CartAction): CartState => {
  switch (action.type) {
    case cartActions.ADD_TO_CART: {
      const { item, quantity, groupId, groupDescription, sfCart, sfCartId, hideToast } = action;
      if (item) {
        const cartStateClone = { ...cartState, sfCartId: sfCartId };
        if (sfCart) {
          cartStateClone.sfCart = sfCart;
        }
        return addToCart(
          cloneDeep(cartStateClone),
          { ...item, isPriceMissing: item.isPriceMissing || isPriceMissing(item) },
          quantity,
          groupId,
          groupDescription,
          hideToast
        );
      } else return cartState;
    }
    case cartActions.REMOVE_FROM_CART: {
      const { item, quantity, groupId } = action;
      if (item && quantity) {
        return removeFromCart(cloneDeep(cartState), item, quantity, groupId);
      } else return cartState;
    }
    case cartActions.SET_ITEM_QUANTITY: {
      const { item, quantity, groupId } = action;
      if (item && quantity) {
        return updateItemQuantity(cloneDeep(cartState), item, quantity, groupId);
      } else return cartState;
    }
    case cartActions.UPDATE_CART_ITEM: {
      const { item, urgent, groupId, procareAllowance, selected } = action;
      return updateCartItem(
        cloneDeep(cartState),
        item as Product,
        urgent as boolean,
        groupId,
        procareAllowance,
        selected
      );
    }
    case cartActions.UPDATE_CART_ITEMS: {
      const { updatedItems } = action;
      return updateCartItems(cloneDeep(cartState), updatedItems as Product[]);
    }
    case cartActions.UPDATE_CART: {
      return updateCart(cloneDeep(cartState), action);
    }
    case cartActions.EMPTY_CART: {
      return emptyCart(cartState);
    }
    case cartActions.VERIFY_CART: {
      const { verificationItems } = action;
      return verifyCart(unverifyCart(cloneDeep(cartState)), verificationItems);
    }
    case cartActions.SET_CART_USER_ID: {
      const { userId } = action;
      return userId ? {...cartState, userId } : defaultCartState;
    }
    case cartActions.DELETE_CART_USER_ID: {
      return defaultCartState;
    }
    case cartActions.DOWNLOAD_CART_CSV: {
      const { t, languageId, cart = cartState } = action;
      downloadCartCSV(cart, t as TFunction, languageId as string);
      return cartState;
    }
    case cartActions.SET_SF_CART: {
      const { sfCart } = action;
      return setSFCart(cloneDeep(cartState), sfCart as SFCart);
    }
    default: {
      return cartState;
    }
  }
};
